import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Drawer, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReactComponent as Funnel } from 'src/Images/icons/common/funnel.svg';
import { ReactComponent as BulletPoints } from 'src/Images/icons/common/bulletpoints.svg';
import { ReactComponent as ExcelIcon } from 'src/Images/icons/common/excel.svg';
import { ReactComponent as Refresh } from 'src/Images/icons/common/refresh.svg';
import { ReactComponent as Search } from 'src/Images/icons/common/search.svg';
import { ReactComponent as ArrowLeft } from 'src/Images/icons/common/arrowleft.svg';
import { ReactComponent as ArrowRight } from 'src/Images/icons/common/arrowright.svg';
import { ReactComponent as Calendar } from 'src/Images/icons/common/calendar.svg';
import Table from 'src/components/Vehicle/List/Table';
import TableContent from 'src/components/Vehicle/List/TableContent';
import TableRightDrawer from 'src/components/Vehicle/List/TableRightDrawer';
import VehicleListMobileFilterDrawer from 'src/components/Vehicle/List/TransporterListMobileFilterDrawer';
import Breadcrumb from 'src/components/Breadcrumb';
import CustomMultiSelect from 'src/utils/CustomMultiSelect';
import { CustomToolTip } from 'src/utils/CustomToolTip';
import getColorCode from 'src/utils/GetThemeColor';
import usePageTitle from 'src/utils/SetPageTitle';
import { useSetBreadcrumbData } from 'src/utils/SetBreadcrumbData';
import { loadExcelExportModules } from 'src/utils/excelExport';
import { getVehicleList } from 'src/redux/Actions/Vehicle';
import { getTransporterDropdownOptions } from 'src/redux/Actions/VehicleTrackingOutward';
import 'src/style/tableList.css';

const DEFAULT_FIELDS = ['1', '2', '3', '4', '5', '6', '7', '8'];
const PREFERENCE_KEY = 'savedVehicleListColumns';

export default function VehicleList() {
  usePageTitle('Vehicles');
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width:767px)');
  const authReducer = useSelector((state) => state.AuthReducer);
  const vehicleList = useSelector((state) => state.VehicleReducer.vehicleList);
  const { transporterDropdownOptions } = useSelector(
    (state) => state.VehicleTrackingOutwardReducer,
  );
  const themeColor = authReducer?.userData?.tenant?.theme;
  const tenantOrganisationId = authReducer?.userData?.organisations?.id;

  const [pageNumber, setPageNumber] = useState(1);
  const [paginationBatchSize, setPaginationBatchSize] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showFunnel, setShowFunnel] = useState(false);
  const [openOrganizer, setOpenOrganizer] = useState(false);
  const [tableSortOrder, setTableSortOrder] = useState(null);
  const [tableSortField, setTableSortField] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [transporterSearchTerm, setTransporterSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    status: '',
    startDate: null,
    endDate: null,
    transporterId: '',
    capacityMin: '',
    capacityMax: '',
    insuranceStartDate: null,
    insuranceEndDate: null,
    fitnessStartDate: null,
    fitnessEndDate: null,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    status: '',
    startDate: null,
    endDate: null,
    transporterId: '',
    capacityMin: '',
    capacityMax: '',
    insuranceStartDate: null,
    insuranceEndDate: null,
    fitnessStartDate: null,
    fitnessEndDate: null,
  });

  const savedPreferences = useMemo(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(PREFERENCE_KEY));
      return {
        selected: saved?.selected || DEFAULT_FIELDS,
        order: saved?.order || [],
      };
    } catch {
      return { selected: DEFAULT_FIELDS, order: [] };
    }
  }, []);
  const [selectedFieldFilter, setSelectedFieldFilter] = useState(
    savedPreferences.selected,
  );
  const [draggedFieldOrder, setDraggedFieldOrder] = useState(
    savedPreferences.order,
  );

  const fieldFilter = vehicleList?.fieldMapping || [];
  const vehicles = vehicleList?.vehicleMasterList || [];
  const totalPages = vehicleList?.totalPages || 1;
  const selectedFields = vehicleList?.selectedFields || [];
  const effectiveFieldOrder =
    draggedFieldOrder.length > 0 ? draggedFieldOrder : selectedFieldFilter;

  const tableHeaders = useMemo(
    () =>
      effectiveFieldOrder
        .map((fieldId) => {
          const selectedField = selectedFields.find(
            (field) => String(field.id) === String(fieldId),
          );
          return selectedField
            ? { ...selectedField, id: fieldId, columnId: fieldId }
            : null;
        })
        .filter(Boolean),
    [effectiveFieldOrder, selectedFields],
  );

  useSetBreadcrumbData(location.pathname, 'Vehicles');

  useEffect(() => {
    if (tenantOrganisationId) {
      dispatch(
        getTransporterDropdownOptions(
          tenantOrganisationId,
          transporterSearchTerm,
          null,
        ),
      );
    }
  }, [dispatch, tenantOrganisationId, transporterSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchInput(searchText);
      setPageNumber(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchText]);

  const fetchVehicles = useCallback(() => {
    if (!tenantOrganisationId) return;
    const params = new URLSearchParams();
    if (appliedFilters.startDate) {
      params.append('start_date', appliedFilters.startDate);
    }
    if (appliedFilters.endDate) {
      params.append('end_date', appliedFilters.endDate);
    }
    if (appliedFilters.status) {
      params.append('status', appliedFilters.status);
    }
    if (appliedFilters.transporterId) {
      params.append('transporter_id', appliedFilters.transporterId);
    }
    if (appliedFilters.capacityMin) {
      params.append('capacity_min', appliedFilters.capacityMin);
    }
    if (appliedFilters.capacityMax) {
      params.append('capacity_max', appliedFilters.capacityMax);
    }
    if (appliedFilters.insuranceStartDate) {
      params.append('insurance_start_date', appliedFilters.insuranceStartDate);
    }
    if (appliedFilters.insuranceEndDate) {
      params.append('insurance_end_date', appliedFilters.insuranceEndDate);
    }
    if (appliedFilters.fitnessStartDate) {
      params.append('fitness_start_date', appliedFilters.fitnessStartDate);
    }
    if (appliedFilters.fitnessEndDate) {
      params.append('fitness_end_date', appliedFilters.fitnessEndDate);
    }

    dispatch(
      getVehicleList(
        tenantOrganisationId,
        pageNumber,
        searchInput,
        tableSortOrder,
        tableSortField,
        effectiveFieldOrder,
        paginationBatchSize,
        params.toString(),
      ),
    );
  }, [
    appliedFilters,
    dispatch,
    effectiveFieldOrder,
    pageNumber,
    paginationBatchSize,
    refreshKey,
    searchInput,
    tableSortField,
    tableSortOrder,
    tenantOrganisationId,
  ]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  useEffect(() => {
    localStorage.setItem(
      PREFERENCE_KEY,
      JSON.stringify({
        selected: selectedFieldFilter,
        order: draggedFieldOrder,
      }),
    );
  }, [draggedFieldOrder, selectedFieldFilter]);

  const toggleOrganizer = (open) => () => setOpenOrganizer(open);

  const handleSelectedFieldFilter = (event, value) => {
    setSelectedFieldFilter((current) =>
      event.target.checked
        ? [...new Set([...current, value])]
        : current.filter((field) => field !== value),
    );
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleResetFilter = () => {
    const emptyFilters = {
      status: '',
      startDate: null,
      endDate: null,
      transporterId: '',
      capacityMin: '',
      capacityMax: '',
      insuranceStartDate: null,
      insuranceEndDate: null,
      fitnessStartDate: null,
      fitnessEndDate: null,
    };
    setFormData(emptyFilters);
    setAppliedFilters(emptyFilters);
    setTransporterSearchTerm('');
    setPageNumber(1);
  };

  const handleApplyFilter = () => {
    setAppliedFilters(formData);
    setPageNumber(1);
    if (isMobile) setShowFunnel(false);
  };

  // Matches LeadList's handleTableSortOrder — single handler stored in state
  // and passed down, instead of an inline arrow function created on every render.
  const handleTableSortOrder = (value, field) => {
    setTableSortOrder(value);
    setTableSortField(field);
    setPageNumber(1);
  };

  const exportToExcel = async () => {
    if (!vehicles.length) return;
    const { utils, write, saveAs } = await loadExcelExportModules();
    const rows = vehicles.map((vehicle) =>
      Object.fromEntries(
        tableHeaders.map((header) => [
          header.label,
          vehicle[header.value] ?? '',
        ]),
      ),
    );
    const worksheet = utils.json_to_sheet(rows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, 'Vehicles');
    const excelBuffer = write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
      new Blob([excelBuffer], { type: 'application/octet-stream' }),
      `Vehicles_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  // Matches LeadList's handleInputChange — validates the typed page number
  // against the total page count before committing it to state.
  const handleInputChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!Number.isNaN(value) && value >= 1 && value <= totalPages) {
      setPageNumber(value);
    }
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const formattedPageNumber = pageNumber < 10 ? `0${pageNumber}` : pageNumber;

  let transporterDataOption = [];
  if (transporterDropdownOptions && transporterDropdownOptions.length > 0) {
    transporterDataOption = transporterDropdownOptions.map(
      (transporterObj) => ({
        value: transporterObj.id,
        label: transporterObj.name,
      }),
    );
  }

  const transporterValue =
    transporterDataOption.find(
      (option) => String(option.value) === String(formData.transporterId),
    ) || '';

  // Matches LeadList's hasUnappliedFilters — compares the working form
  // state against what was last applied so the "Unapplied Filters" badge
  // can be shown in the desktop filter panel header.
  const hasUnappliedFilters = () =>
    Object.keys(formData).some((key) => formData[key] !== appliedFilters[key]);

  return (
    <div className="div-flex-column" data-testid="vehicles-page">
      <div className="page-layout div-flex-column">
        <div className="div-space-between slider-padding left-padding">
          <div className="div-flex-row cg-5">
            <Breadcrumb />
          </div>
          <Col xxl={2} xl={2} lg={4} md={5} sm={7} xs={7} className="p-0 m-0">
            <div className="input-wrapper">
              <input
                type="text"
                data-testid="vehicles-search-input"
                className="form-input-section input-icon-remove"
                placeholder="Search vehicle"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
              <span className="form-input-section-icon">
                <Search
                  className="icon-size-lg"
                  color={getColorCode(themeColor)}
                />
              </span>
            </div>
          </Col>
        </div>
        <div className="div-space-between border-bottom slider-padding">
          <span className="text-xxl text-bold left-padding">Vehicles</span>
          <div className="div-flex-row div-align-center cg-20">
            <div className="div-flex-row cg-20 div-align-center">
              <div className="list-container div-flex-column rg-5">
                <CustomToolTip title="Filter" placement="bottom">
                  <button
                    data-testid="filter-open-btn"
                    className="icon-btn-none"
                    aria-label="filter"
                    onClick={() => {
                      setShowFunnel((current) => !current);
                      if (isMobile) setShowFunnel(true);
                    }}
                  >
                    <Funnel
                      className="icon-size-lg"
                      color={showFunnel ? getColorCode(themeColor) : '#9F9F9F'}
                    />
                  </button>
                </CustomToolTip>
              </div>
              <div className="button-container">
                <CustomToolTip title="Organiser" placement="bottom">
                  <button
                    data-testid="organizer-open-btn"
                    className="icon-btn-none"
                    aria-label="organizer"
                    onClick={toggleOrganizer(true)}
                  >
                    <BulletPoints className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              <Drawer
                anchor="right"
                open={openOrganizer}
                onClose={toggleOrganizer(false)}
                ModalProps={{
                  keepMounted: false,
                }}
                sx={{ zIndex: 2 }}
              >
                <div className="right-drawer-component-small">
                  <TableRightDrawer
                    toggleDrawer={toggleOrganizer}
                    fieldFilter={fieldFilter}
                    selectedFieldFilter={selectedFieldFilter}
                    fieldOrder={effectiveFieldOrder}
                    setDraggedFieldOrder={setDraggedFieldOrder}
                    handleSelectedFieldFilter={handleSelectedFieldFilter}
                  />
                </div>
              </Drawer>
              <div className="button-container">
                <CustomToolTip title="Export Excel" placement="bottom">
                  <button
                    aria-label="export excel"
                    className="icon-btn-none"
                    onClick={exportToExcel}
                  >
                    <ExcelIcon className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              </div>
              {!isMobile && (
                <CustomToolTip title="Refresh" placement="bottom">
                  <button
                    aria-label="refresh"
                    className="icon-btn-none"
                    onClick={() => setRefreshKey((current) => current + 1)}
                  >
                    <Refresh className="icon-size-lg" color="#9F9F9F" />
                  </button>
                </CustomToolTip>
              )}
            </div>
            {!isMobile && (
              <select
                value={paginationBatchSize}
                onChange={(event) => {
                  setPaginationBatchSize(Number(event.target.value));
                  setPageNumber(1);
                }}
                className="filter-input-section"
              >
                {[10, 20, 30, 40, 50, 100, 500, 1000].map((size) => (
                  <option value={size} key={size}>
                    {size}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
        <Row className="g-0">
          <Col
            xxl={showFunnel ? 10 : 12}
            xl={showFunnel ? 9 : 12}
            lg={showFunnel ? 8 : 12}
            md={showFunnel ? 8 : 12}
            sm={showFunnel ? 0 : 12}
            xs={showFunnel ? 0 : 12}
            className="border-bottom"
          >
            <Table
              headers={tableHeaders}
              minCellWidth={150}
              tableContent={
                <TableContent headers={tableHeaders} vehicles={vehicles} />
              }
              tableSortOrder={tableSortOrder}
              handleTableSortOrder={handleTableSortOrder}
            />
            <div className="footer-container">
              <div className="div-space-between div-align-center footer-content-for-page">
                <div className="div-flex-row cg-20 pagination-container div-align-center">
                  <div className="div-flex-row cg-10 div-align-center">
                    <button
                      data-testid="pagination-prev"
                      className="icon-btn-none"
                      onClick={handlePreviousPage}
                      aria-label="previous page"
                    >
                      <ArrowLeft
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                    <input
                      className="page-number-container input-icon-remove text-sm"
                      data-testid="pagination-input"
                      value={formattedPageNumber}
                      onChange={handleInputChange}
                      type="number"
                      onWheel={(e) => e.target.blur()}
                      max={totalPages}
                    />
                    <button
                      data-testid="pagination-next"
                      className="icon-btn-none"
                      onClick={handleNextPage}
                      aria-label="next page"
                    >
                      <ArrowRight
                        className="icon-size-sm"
                        color={getColorCode(themeColor)}
                      />
                    </button>
                  </div>
                  <span className="text-grey text-sm">
                    Page {pageNumber} of {totalPages}
                  </span>
                </div>
              </div>
            </div>
          </Col>
          {showFunnel && !isMobile && (
            <Col
              xxl={showFunnel ? 2 : 0}
              xl={showFunnel ? 3 : 0}
              lg={showFunnel ? 4 : 0}
              md={showFunnel ? 4 : 0}
              sm={showFunnel ? 12 : 0}
              xs={showFunnel ? 12 : 0}
              className="border-left border-bottom"
            >
              <div
                className="filter-list-item div-flex-row div-space-between div-align-center border-bottom"
                data-testid="filter-drawer"
              >
                <span className="text-sm text-semi-bold">Filter</span>
                {hasUnappliedFilters() && (
                  <div className="text-yellow text-xs text-semi-bold">
                    ⚠️ Unapplied Filters
                  </div>
                )}
              </div>
              <div className="div-flex-column div-space-between rg-20">
                <div className="filter-list-item-container-scroll div-flex-column rg-15">
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Status</span>
                    <select
                      name="status"
                      value={formData.status || ''}
                      onChange={handleFilterChange}
                      className="form-input-section"
                    >
                      <option value="">Select Status</option>
                      <option value="1">Idle</option>
                      <option value="2">Scheduled</option>
                      <option value="3">In Progress</option>
                    </select>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Start Date</span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="startDate"
                        value={formData.startDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">End Date</span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="endDate"
                        value={formData.endDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Transporter Id
                    </span>
                    <div className="input-wrapper">
                      <CustomMultiSelect
                        name="transporterId"
                        isMulti={false}
                        className="multi-select-input-section"
                        onChange={(selectedOption) => {
                          setFormData((current) => ({
                            ...current,
                            transporterId: selectedOption?.value || '',
                          }));
                          setTransporterSearchTerm('');
                        }}
                        onInputChange={(inputValue, { action }) => {
                          if (action === 'input-change') {
                            setTransporterSearchTerm(inputValue);
                          }
                        }}
                        options={transporterDataOption}
                        classNamePrefix="react-select"
                        placeholder="Search Transporter Id"
                        value={transporterValue}
                        onBlur={() => setTransporterSearchTerm('')}
                      />
                      {!formData.transporterId && (
                        <span className="form-input-section-icon">
                          <Search className="icon-size-sm" color="#9F9F9F" />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Capacity Min</span>
                    <input
                      type="number"
                      name="capacityMin"
                      value={formData.capacityMin || ''}
                      onChange={handleFilterChange}
                      className="form-input-section"
                    />
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">Capacity Max</span>
                    <input
                      type="number"
                      name="capacityMax"
                      value={formData.capacityMax || ''}
                      onChange={handleFilterChange}
                      className="form-input-section"
                    />
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Insurance Start Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="insuranceStartDate"
                        value={formData.insuranceStartDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Insurance End Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="insuranceEndDate"
                        value={formData.insuranceEndDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Fitness / Inspection Start Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="fitnessStartDate"
                        value={formData.fitnessStartDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                  <div className="filter-list-item div-flex-column rg-5">
                    <span className="text-sm text-semi-bold">
                      Fitness / Inspection End Date
                    </span>
                    <div className="input-wrapper">
                      <input
                        type="date"
                        name="fitnessEndDate"
                        value={formData.fitnessEndDate || ''}
                        onChange={handleFilterChange}
                        className="form-input-section input-icon-date"
                      />
                      <span className="form-input-section-icon">
                        <Calendar
                          className="icon-size-sm"
                          color={getColorCode(themeColor)}
                        />
                      </span>
                    </div>
                  </div>
                </div>
                <div className="div-flex-row div-space-between div-align-center border-top filter-list-item footer-content-for-component">
                  <button
                    className={`common-btn-outlined border-${themeColor}`}
                    onClick={handleResetFilter}
                    data-testid="filter-reset-btn"
                  >
                    Reset
                  </button>
                  <button
                    className={`common-btn theme-background-${themeColor}`}
                    onClick={handleApplyFilter}
                    data-testid="filter-apply-btn"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </Col>
          )}
        </Row>
        <Drawer
          anchor="right"
          open={isMobile && showFunnel}
          onClose={() => setShowFunnel(false)}
          ModalProps={{
            keepMounted: false,
          }}
          sx={{ zIndex: 2 }}
        >
          <div className="right-drawer-component-small">
            <VehicleListMobileFilterDrawer
              formData={formData}
              handleSearchInputChange={handleFilterChange}
              handleResetFilter={handleResetFilter}
              handleApplyFilter={handleApplyFilter}
              closeFilter={() => setShowFunnel(false)}
              transporterDataOption={transporterDataOption}
              transporterValue={transporterValue}
              setTransporterSearchTerm={setTransporterSearchTerm}
            />
          </div>
        </Drawer>
      </div>
    </div>
  );
}
